import { peekTenantRuntime } from '../config/tenant';
import { logToCloud } from './cloudLoggingService';

// No-Req: coordination security task, 2026-10-23; observe CSP before enforcement.
const MAX_REPORTS_PER_PAGE = 20;
const reported = new Set<string>();
let installed = false;

function originOf(uri: string): string | undefined {
  try {
    const origin = new URL(uri).origin;
    return origin === 'null' ? undefined : origin;
  } catch {
    return undefined;
  }
}

function blockedOriginOf(uri: string): string {
  if (['inline', 'eval', 'data', 'blob'].includes(uri)) return uri;
  // These schemes can contain payloads or object identifiers, never log them.
  if (uri?.toLowerCase().startsWith('data:')) return 'data';
  if (uri?.toLowerCase().startsWith('blob:')) return 'blob';
  return originOf(uri) ?? 'unparsable';
}

function reportViolation(event: SecurityPolicyViolationEvent): void {
  try {
    if (reported.size >= MAX_REPORTS_PER_PAGE) return;
    const effectiveDirective = event.effectiveDirective || event.violatedDirective;
    const disposition = event.disposition;
    if (typeof effectiveDirective !== 'string' || !effectiveDirective
      || (disposition !== 'report' && disposition !== 'enforce')) return;

    const blockedOrigin = blockedOriginOf(event.blockedURI);
    const key = JSON.stringify([effectiveDirective, blockedOrigin, disposition]);
    if (reported.has(key)) return;

    const metadata: Record<string, string> = { effectiveDirective, blockedOrigin, disposition };
    const sourceOrigin = event.sourceFile ? originOf(event.sourceFile) : undefined;
    if (sourceOrigin) metadata.sourceOrigin = sourceOrigin;
    const tenant = peekTenantRuntime()?.tenantId;
    if (tenant) metadata.tenant = tenant;

    // Reserve before handing off, including if logging itself triggers a violation.
    reported.add(key);
    logToCloud('warn', 'CSP violation', 'csp', metadata);
  } catch {
    // Observability must never interrupt the app or create recursive error logs.
  }
}

export function installCspViolationReporter(): void {
  if (installed) return;
  document.addEventListener('securitypolicyviolation', reportViolation);
  installed = true;
}
